import React, { useState, useEffect } from "react";
import {
  Row,
  Col,
  Spin,
  Form,
  Card,
  Select,
  DatePicker,
  InputNumber,
  Input,
} from "antd";
import { InfoCircleOutlined } from "@ant-design/icons";
import { Tooltip } from "antd";
import { connect } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import dayjs from "dayjs";

import MainLayout from "../../../components/layouts/enhancement/MainLayout";
import TopContent from "../../../components/layouts/enhancement/TopContent";
import ChevronDown from "@untitled-ui/icons-react/build/esm/ChevronDown";
import cnt from "../../../services/definedConstant";
import { rightAction } from "../../../redux/actions";
import {
  labelConfig,
  staticConfig,
  apiConfig,
} from "../treasuryContractBooking.config";
import { BOM } from "../../../services/definedMethod";

const INSTRUMENT_TYPE_OPTIONS = [
  { label: "T-Bill",        value: "T-Bill" },
  { label: "Treasury Bond", value: "Treasury Bond" },
  { label: "FGN Bond",      value: "FGN Bond" },
];

const CURRENCY_OPTIONS = [
  { label: "NGN", value: "NGN" },
  { label: "USD", value: "USD" },
  { label: "EUR", value: "EUR" },
  { label: "GBP", value: "GBP" },
];

const ISSUING_AUTHORITY_OPTIONS = [
  { label: "Government",   value: "Government" },
  { label: "Central Bank", value: "Central Bank" },
  { label: "DMO",          value: "DMO" },
];

// ── Mock data — replace with real API calls when endpoints are ready ──────
const MOCK_PRODUCT_OPTIONS = [
  { label: "T-Bill Product (ID: 37)",        value: 37 },
  { label: "FGN Bond Product (ID: 38)",      value: 38 },
  { label: "Treasury Bond Product (ID: 39)", value: 39 },
];

const MOCK_SECURITY_MASTER_OPTIONS = [
  { label: "91-Day T-Bill (ID: 1)",    value: 1 },
  { label: "182-Day T-Bill (ID: 2)",   value: 2 },
  { label: "364-Day T-Bill (ID: 3)",   value: 3 },
  { label: "5-Year FGN Bond (ID: 4)",  value: 4 },
  { label: "10-Year FGN Bond (ID: 5)", value: 5 },
];
// ─────────────────────────────────────────────────────────────────────────

// Converts [2026, 3, 16] → dayjs object for DatePicker
const parseDateArray = (arr) => {
  if (!Array.isArray(arr) || arr.length < 3) return null;
  const [y, m, d] = arr;
  return dayjs(
    `${y}-${String(m).padStart(2, "0")}-${String(d).padStart(2, "0")}`
  );
};

const AddAuction = ({ userData }) => {
  const [form]      = Form.useForm();
  const [isLoading, setIsLoading] = useState(false);
  const { session } = userData || {};
  const navigate    = useNavigate();
  const location    = useLocation();

  // If location.state has an id → edit mode, otherwise → create mode
  const auctionId = location.state || null;
  const isEdit    = !!auctionId;

  // ── Load existing data when editing ──────────────────────────────────────
  useEffect(() => {
    if (isEdit) fetchAuctionDetail(auctionId);
  }, [auctionId]);

  const fetchAuctionDetail = (id) => {
    setIsLoading(true);

    const body = { session };
    const url  = `${apiConfig.GetAuctions}/auctions/${id}`;

    BOM.FetchReqAction(body, url, (err, res) => {
      if (err) {
        BOM.AlertMsg(
          cnt.DANGER,
          err?.defaultUserMessage || "Failed to load auction details."
        );
        setIsLoading(false);
        return;
      }

      form.setFieldsValue({
        productId:        res.productId        ?? null,
        securityMasterId: res.securityMasterId ?? null,
        auctionCode:      res.auctionCode,
        instrumentType:   res.instrumentType,
        currencyCode:     res.currencyCode,
        issuingAuthority: res.issuingAuthority,
        totalOffered:     res.totalOffered     ?? null,
        auctionDate:      parseDateArray(res.auctionDate),
        settlementDate:   parseDateArray(res.settlementDate),
        maturityDate:     res.maturityDate ? parseDateArray(res.maturityDate) : null,
      });

      setIsLoading(false);
    });
  };

  // ── Submit: POST for create, PUT for edit ─────────────────────────────────
  const onFinish = (values) => {
    const body = {
      method:           isEdit ? "PUT" : "POST",
      productId:        values.productId,
      securityMasterId: values.securityMasterId,
      auctionDate:      values.auctionDate?.format("YYYY-MM-DD"),
      auctionCode:      values.auctionCode,
      settlementDate:   values.settlementDate?.format("YYYY-MM-DD"),
      maturityDate:     values.maturityDate?.format("YYYY-MM-DD"),
      totalOffered:     values.totalOffered,
      currencyCode:     values.currencyCode,
      instrumentType:   values.instrumentType,
      issuingAuthority: values.issuingAuthority,
      locale:           "en",
      dateFormat:       "yyyy-MM-dd",
      session,
      ...(isEdit && { auctionId }), // include id only on edit
    };

    const url = isEdit
      ? `${apiConfig.GetAuctions}/auctions/${auctionId}`
      : `${apiConfig.GetAuctions}/auctions`;

    setIsLoading(true);
    BOM.FetchReqAction(body, url, (err, res) => {
      if (err) {
        BOM.AlertMsg(
          cnt.DANGER,
          err?.defaultUserMessage ||
            (isEdit ? "Failed to update auction." : "Failed to create auction.")
        );
        setIsLoading(false);
        return;
      }
      BOM.AlertMsg(
        cnt.SUCCESS,
        isEdit ? "Auction updated successfully." : "Auction created successfully."
      );
      setIsLoading(false);
      navigate(-1);
    });
  };

  const Tip = ({ text, tip }) => (
    <span>
      {text}
      <Tooltip title={tip}>
        <InfoCircleOutlined style={{ marginLeft: 4, color: "#667085" }} />
      </Tooltip>
    </span>
  );

  return (
    <MainLayout pageItems={staticConfig.newAuction}>
      <TopContent pageTitle={labelConfig.auction} />

      <Spin spinning={isLoading} size="large">
        <div style={{ padding: "24px" }}>
          <Card
            style={{
              boxShadow: "0px 0px 30px 3px rgba(0,0,0,0.1)",
              borderRadius: 20,
            }}
          >
            <h3 style={{ marginBottom: 24, fontWeight: 600 }}>
              {isEdit ? "Edit Auction" : "Create Auction"}
            </h3>

            <Form layout="vertical" form={form} onFinish={onFinish}>

              {/* Product & Security Master */}
              <Row>
                <Col span={12} style={{ padding: "0 8px" }}>
                  <Form.Item
                    label={<Tip text="Product" tip="The financial product associated with this auction." />}
                    name="productId"
                    rules={[{ required: true, message: "Product is required" }]}
                  >
                    <Select
                      style={{ height: 44 }}
                      suffixIcon={<ChevronDown color="#667085" />}
                      options={MOCK_PRODUCT_OPTIONS}
                      placeholder="Select Product"
                      showSearch
                      optionFilterProp="label"
                    />
                  </Form.Item>
                </Col>
                <Col span={12} style={{ padding: "0 8px" }}>
                  <Form.Item
                    label={<Tip text="Security Master" tip="The security master record linked to this auction." />}
                    name="securityMasterId"
                    rules={[{ required: true, message: "Security Master is required" }]}
                  >
                    <Select
                      style={{ height: 44 }}
                      suffixIcon={<ChevronDown color="#667085" />}
                      options={MOCK_SECURITY_MASTER_OPTIONS}
                      placeholder="Select Security Master"
                      showSearch
                      optionFilterProp="label"
                    />
                  </Form.Item>
                </Col>
              </Row>

              {/* Auction Code & Instrument Type */}
              <Row>
                <Col span={12} style={{ padding: "0 8px" }}>
                  <Form.Item
                    label={<Tip text="Auction Code" tip="A unique code to identify this auction." />}
                    name="auctionCode"
                    rules={[{ required: true, message: "Auction code is required" }]}
                  >
                    <Input style={{ height: 44 }} placeholder="e.g. NGN-T-Bill" />
                  </Form.Item>
                </Col>
                <Col span={12} style={{ padding: "0 8px" }}>
                  <Form.Item
                    label={<Tip text="Instrument Type" tip="The type of instrument being auctioned." />}
                    name="instrumentType"
                    rules={[{ required: true, message: "Instrument type is required" }]}
                  >
                    <Select
                      style={{ height: 44 }}
                      suffixIcon={<ChevronDown color="#667085" />}
                      options={INSTRUMENT_TYPE_OPTIONS}
                      placeholder="Select Instrument Type"
                    />
                  </Form.Item>
                </Col>
              </Row>

              {/* Currency & Issuing Authority */}
              <Row>
                <Col span={12} style={{ padding: "0 8px" }}>
                  <Form.Item
                    label={<Tip text="Currency" tip="The currency in which this auction is denominated." />}
                    name="currencyCode"
                    rules={[{ required: true, message: "Currency is required" }]}
                  >
                    <Select
                      style={{ height: 44 }}
                      suffixIcon={<ChevronDown color="#667085" />}
                      options={CURRENCY_OPTIONS}
                      placeholder="Select Currency"
                    />
                  </Form.Item>
                </Col>
                <Col span={12} style={{ padding: "0 8px" }}>
                  <Form.Item
                    label={<Tip text="Issuing Authority" tip="The authority responsible for issuing the instrument." />}
                    name="issuingAuthority"
                    rules={[{ required: true, message: "Issuing authority is required" }]}
                  >
                    <Select
                      style={{ height: 44 }}
                      suffixIcon={<ChevronDown color="#667085" />}
                      options={ISSUING_AUTHORITY_OPTIONS}
                      placeholder="Select Issuing Authority"
                    />
                  </Form.Item>
                </Col>
              </Row>

              {/* Total Offered */}
              <Row>
                <Col span={12} style={{ padding: "0 8px" }}>
                  <Form.Item
                    label={<Tip text="Total Offered" tip="The total amount being offered in this auction." />}
                    name="totalOffered"
                    rules={[
                      { required: true, message: "Total offered is required" },
                      { type: "number", min: 1, message: "Amount must be greater than 0" },
                    ]}
                  >
                    <InputNumber
                      style={{ width: "100%", height: 44 }}
                      placeholder="e.g. 1,000,000,000.00"
                      formatter={(v) =>
                        v ? `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",") : ""
                      }
                      parser={(v) => v?.replace(/,/g, "")}
                      precision={2}
                      min={0}
                    />
                  </Form.Item>
                </Col>
              </Row>

              {/* Auction Date & Settlement Date */}
              <Row>
                <Col span={12} style={{ padding: "0 8px" }}>
                  <Form.Item
                    label={<Tip text="Auction Date" tip="The date on which the auction takes place." />}
                    name="auctionDate"
                    rules={[{ required: true, message: "Auction date is required" }]}
                  >
                    <DatePicker
                      style={{ width: "100%", height: 44 }}
                      format="YYYY-MM-DD"
                      placeholder="YYYY-MM-DD"
                    />
                  </Form.Item>
                </Col>
                <Col span={12} style={{ padding: "0 8px" }}>
                  <Form.Item
                    label={<Tip text="Settlement Date" tip="The date on which the auction settles." />}
                    name="settlementDate"
                    rules={[{ required: true, message: "Settlement date is required" }]}
                  >
                    <DatePicker
                      style={{ width: "100%", height: 44 }}
                      format="YYYY-MM-DD"
                      placeholder="YYYY-MM-DD"
                    />
                  </Form.Item>
                </Col>
              </Row>

              {/* Maturity Date */}
              <Row>
                <Col span={12} style={{ padding: "0 8px" }}>
                  <Form.Item
                    label={<Tip text="Maturity Date" tip="The date on which the auctioned instrument matures." />}
                    name="maturityDate"
                    rules={[{ required: true, message: "Maturity date is required" }]}
                  >
                    <DatePicker
                      style={{ width: "100%", height: 44 }}
                      format="YYYY-MM-DD"
                      placeholder="YYYY-MM-DD"
                    />
                  </Form.Item>
                </Col>
              </Row>

              <div style={{ borderTop: "1px solid #EAECF0", margin: "8px 0 20px" }} />

              <Row justify="end">
                <Col>
                  <button
                    type="button"
                    onClick={() => navigate(-1)}
                    style={{
                      height: 44,
                      padding: "0 24px",
                      borderRadius: 8,
                      border: "1px solid #D0D5DD",
                      background: "#fff",
                      color: "#344054",
                      fontWeight: 600,
                      fontSize: 14,
                      cursor: "pointer",
                      marginRight: 12,
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={() => form.submit()}
                    style={{
                      height: 44,
                      padding: "0 24px",
                      borderRadius: 8,
                      border: "1px solid #BA24D5",
                      background: "#BA24D5",
                      color: "#fff",
                      fontWeight: 600,
                      fontSize: 14,
                      cursor: "pointer",
                    }}
                  >
                    {isEdit ? "Update" : "Submit"}
                  </button>
                </Col>
              </Row>

              <button type="submit" hidden />
            </Form>
          </Card>
        </div>
      </Spin>
    </MainLayout>
  );
};

const mapStateToProps = (state) => ({
  userData: state.AuthenticationData.userData,
  rightBtn: state.RightSideBtn,
});

export default connect(mapStateToProps, {})(AddAuction);